import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { v4 as uuidv4 } from 'uuid';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environment';
import { emsCompetition, emsPlayer, emsTeam } from 'src/models/emsModels';
import { NotificationBar } from 'src/services/notification.service';
import { ConfirmDialogDialog } from '../confirm-dialog/confirm-dialog.component';
import { PlayerCreateComponent } from '../player-create/player-create.component';
import { PlayerEditComponent } from '../player-edit/player-edit.component';
import { AngularFireFunctions } from '@angular/fire/compat/functions';

@Component({
  selector: 'app-team-edit',
  templateUrl: './team-edit.component.html',
  styleUrl: './team-edit.component.scss'
})
export class TeamEditComponent {

  notificationBar: NotificationBar;
  teamGroup = new FormGroup({
    nameControl: new FormControl('', [Validators.required]),
    tricodeControl: new FormControl('', [Validators.required]),
    competitionControl: new FormControl('', [Validators.required]),
    logoControl: new FormControl('', []),
    coachControl: new FormControl('', []),
    coachpictureControl: new FormControl('', []),
    asscoachControl: new FormControl('', []),
    asscoachpictureControl: new FormControl('', []),
    notesControl: new FormControl('', []),
  });
  isProcessingDelete = false;
  isProcessingDuplication = false;
  isProcessingLogoDelete = false;
  workspaceID = "";
  teamID = "";
  logoUploadProgress = 0;
  coachPicUploadProgress = 0;
  asscoachPicUploadProgress = 0;
  playerList = new Array<emsPlayer>();
  isAddingPlayer = false;
  isEditPlayer = "";
  isDeletePlayer = ""; 
  isDeleteCoachPic = false;
  isDeleteAssCoachPic = false;
  competitions = Array<emsCompetition>();
  placeholderImage = environment.placeholderImage;
  placeholderTeamLogo = environment.placeholderTeamLogo;
  placeholderPersonImage = environment.placeholderPersonImage;
  competitionsDBRef: any;
  teamsDBRef: any;
  playerDBRef: any;

  constructor(private title: Title, private router: Router, private route: ActivatedRoute, private location: Location, private snackBar: MatSnackBar, public dialog: MatDialog, private fireStorage: AngularFireStorage, private db: AngularFireDatabase, private fireFunctions: AngularFireFunctions) {
    this.notificationBar = new NotificationBar(snackBar);
   }

  ngOnInit() {  
    this.route.paramMap.subscribe(params => {
      this.workspaceID  = this.location.path().split("/")[1];
      this.teamID  = this.location.path().split("/")[3];
      this.loadTeam();
      this.loadPlayer();
      this.loadCompetitions();
    });
  }

  ngOnDestroy() {
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
    if (this.playerDBRef) {
      this.playerDBRef.off('value');
    };
  }

  loadCompetitions() {
    this.competitionsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions').orderByChild('workspace').equalTo(this.workspaceID);
    this.competitionsDBRef.on('value', x => {
      this.competitions = new Array<emsCompetition>();
      x.forEach( x => {
        var competition = x.toJSON() as emsCompetition;
        competition.uid = x.key;
        this.competitions.push(competition);
      });
      this.competitions.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  getSelectedCompetition(uid: string) {
    return this.competitions.find(competition => competition.uid == uid);
  }

  loadTeam() {
    this.teamsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID);
    this.teamsDBRef.on('value', x => {
      var team = x.toJSON() as emsTeam;
      this.title.setTitle(team.name + " - Team - EsportsMS");
      this.teamGroup.controls.nameControl.setValue(team.name);
      this.teamGroup.controls.tricodeControl.setValue(team.tricode);
      this.teamGroup.controls.competitionControl.setValue(team.competition);
      this.teamGroup.controls.logoControl.setValue(team.logo);
      this.teamGroup.controls.coachControl.setValue(team.coach);
      this.teamGroup.controls.coachpictureControl.setValue(team.coachPicture);
      this.teamGroup.controls.asscoachControl.setValue(team.asscoach);
      this.teamGroup.controls.asscoachpictureControl.setValue(team.asscoachPicture);
      this.teamGroup.controls.notesControl.setValue(team.notes);
    });
  }

  loadPlayer() {
    this.playerDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/player').orderByChild('team').equalTo(this.teamID);
    this.playerDBRef.on('value', x => {
      const rawData = x.toJSON(); // different because /player is an list, for object I would need /player/{teamID}
      this.playerList = new Array<emsPlayer>();
      Object.keys(rawData).forEach(key => { 
        var player = rawData[key] as emsPlayer;
        player.uid = key
        this.playerList.push(player);
      });
      this.playerList.sort((player1, player2) => player1.position - player2.position);
    });
  }

  changeName() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      name: this.teamGroup.controls.nameControl.value
    });
  }

  changeTircode() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      tricode: this.teamGroup.controls.tricodeControl.value
    });
  }

  changeCompetition() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      competition: this.teamGroup.controls.competitionControl.value
    });
  }

  async uploadLogo(event: any) {
    if (this.teamGroup.controls.logoControl.value && this.teamGroup.controls.logoControl.value.length > 0) {
      await this.deleteLogo(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/t/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.logoUploadProgress = progress
      console.log(this.logoUploadProgress);
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        var fileUrl = "";
        fileRef.getMetadata().subscribe( x => {
          fileUrl = environment.assetDomain + `${x.fullPath.split('.').slice(0,-1).join('.')}_400x400.png`;
        })
        this.logoUploadProgress = 0;
        setTimeout(() => {
          this.teamGroup.controls.logoControl.setValue(fileUrl);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
            logo: fileUrl
          });
        }, 1500);
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.logoUploadProgress = 0;
      }
    });
  }

  async uploadCoachPic(event: any) {
    if (this.teamGroup.controls.coachpictureControl.value && this.teamGroup.controls.coachpictureControl.value.length > 0) {
      await this.deleteCoachPic(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/t/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.coachPicUploadProgress = progress
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        var fileUrl = "";
        fileRef.getMetadata().subscribe( x => {
          fileUrl = environment.assetDomain + `${x.fullPath.split('.').slice(0,-1).join('.')}_400x400.png`;
        })
        this.coachPicUploadProgress = 0;
        setTimeout(() => {
          this.teamGroup.controls.coachpictureControl.setValue(fileUrl);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
            coachPicture: fileUrl
          });
        }, 1500);
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.coachPicUploadProgress = 0;
      }
    });
  }

  async uploadAssCoachPic(event: any) {
    if (this.teamGroup.controls.asscoachpictureControl.value && this.teamGroup.controls.asscoachpictureControl.value.length > 0) {
      await this.deleteAssCoachPic(true);
    }
    var fileType = event.target.files[0].name.split('.').pop();
    fileType = "." + fileType;
    const fileRef = this.fireStorage.ref("/resources/esportsms/w/" + this.workspaceID + "/t/" + `${uuidv4()}` + fileType);
    const task = fileRef.put(event.target.files[0]);
    task.percentageChanges().subscribe((progress) => {
      this.asscoachPicUploadProgress = progress
    });
    task.snapshotChanges().subscribe({
      complete: () => {
        var fileUrl = "";
        fileRef.getMetadata().subscribe( x => {
          fileUrl = environment.assetDomain + `${x.fullPath.split('.').slice(0,-1).join('.')}_400x400.png`;
        })
        this.asscoachPicUploadProgress = 0;
        setTimeout(() => {
          this.teamGroup.controls.asscoachpictureControl.setValue(fileUrl);
          this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
            asscoachPicture: fileUrl
          });
        }, 1500);
      },
      error: (error) => {
        this.notificationBar.show("Upload failed!")
        this.asscoachPicUploadProgress = 0;
      }
    });
  }

  changeCoach() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      coach: this.teamGroup.controls.coachControl.value
    });
  }

  changeAssCoach() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      asscoach: this.teamGroup.controls.asscoachControl.value
    });
  }

  async deleteLogo(skipConfirm: boolean) {
    this.isProcessingLogoDelete = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
        data: {
          actionType: "delete",
          message: "Are you sure you want to delete the team logo?",
        }
      });
      res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }
    if (res == true) {
      var picturePath = new URL(this.teamGroup.controls.logoControl.value).pathname;
      const fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.teamGroup.controls.logoControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
        logo: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Logo deleted!");
      }
    }
    this.isProcessingLogoDelete = false;
  }

  async deleteCoachPic(skipConfirm: boolean) {
    this.isDeleteCoachPic = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
        data: {
          actionType: "delete",
          message: "Are you sure you want to delete the picture of the coach?",
        }
      });
      res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }
    if (res == true) {
      var picturePath = new URL(this.teamGroup.controls.coachpictureControl.value).pathname;
      const fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.teamGroup.controls.coachpictureControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
        coachPicture: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Coach picture deleted!");
      }
    };
    this.isDeleteCoachPic = false;
  }

  async deleteAssCoachPic(skipConfirm: boolean) {
    this.isDeleteAssCoachPic = true;
    var res = false;
    if (skipConfirm == false) {
      var dialogRef = this.dialog.open(ConfirmDialogDialog, {
        data: {
          actionType: "delete",
          message: "Are you sure you want to delete the picture of the assistant coach?",
        }
      });
      res = await dialogRef.afterClosed().toPromise();
    }
    else {
      res = true;
    }
    if (res == true) {
      var picturePath = new URL(this.teamGroup.controls.asscoachpictureControl.value).pathname;
      const fileRef = this.fireStorage.ref(picturePath);
      fileRef.delete();
      this.teamGroup.controls.asscoachpictureControl.setValue("");
      this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
        asscoachPicture: ""
      });
      if (skipConfirm == false) {
        this.notificationBar.show("Assistant coach picture deleted!");
      }
    };
    this.isDeleteAssCoachPic = false;
  }

  changeNotes() {
    this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).update({
      notes: this.teamGroup.controls.notesControl.value
    });
  }

  async addPlayer() {
    this.isAddingPlayer = true;
    var dialogRef = this.dialog.open(PlayerCreateComponent, {
      data: {
        workspaceID: this.workspaceID,
        teamID: this.teamID,
      }
    });
    const res = await dialogRef.afterClosed().toPromise();
    this.isAddingPlayer = false;
    var addedPlayer = this.playerList.find(x => x.uid == res)
    this.editPlayer(addedPlayer);
  }

  async editPlayer(player: emsPlayer) {
    this.isEditPlayer = player.uid;
    var dialogRef = this.dialog.open(PlayerEditComponent, {
      data: {
        workspaceID: this.workspaceID,
        teamID: this.teamID,
        playerID: player.uid,
      }
    });
    const res = await dialogRef.afterClosed().toPromise();
    this.isEditPlayer = "";
  }
  
  async deletePlayer(player: emsPlayer) {
    this.isDeletePlayer = player.uid;
    var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "delete",
        message: "Are you sure you want to delete " + player.name + "?",
      }
    });
    return dialogRef.afterClosed().toPromise().then(async res => {
      if (res == true) {
        try {
          var picturePath = new URL(player.picture).pathname;
          var fileRef = this.fireStorage.ref(picturePath);
          fileRef.delete();
        }
        catch { }
        this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + player.uid).remove().then( x => {
          this.notificationBar.show("Player deleted!");
        }, err => {
            this.notificationBar.showUnknownError();
        })
      };
      this.isDeletePlayer = "";
    });
  }

  async deleteTeam() {
    this.isProcessingDelete = true;
    var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "delete",
        message: "Are you sure you want to delete " + this.teamGroup.controls.nameControl.value + "?",
      }
    });
    return dialogRef.afterClosed().toPromise().then(async res => {
      if (res == true) {
        if (this.teamGroup.controls.logoControl.value) {
          var logoPath = new URL(this.teamGroup.controls.logoControl.value).pathname;
          var fileRef = this.fireStorage.ref(logoPath);
          fileRef.delete();
        }
        if (this.teamGroup.controls.coachpictureControl.value) {
          var coachPicturePath = new URL(this.teamGroup.controls.coachpictureControl.value).pathname;
          fileRef = this.fireStorage.ref(coachPicturePath);
          fileRef.delete();
        }
        if (this.teamGroup.controls.asscoachpictureControl.value) {
          var assCoachPicturePath = new URL(this.teamGroup.controls.asscoachpictureControl.value).pathname;
          fileRef = this.fireStorage.ref(assCoachPicturePath);
          fileRef.delete();
        }
        this.playerList.forEach(player => {
          if (player.picture) {
            var picturePath = new URL(player.picture).pathname;
            var fileRef = this.fireStorage.ref(picturePath);
            fileRef.delete();
          }          
          this.db.database.app.database(environment.emsDatabaseURL).ref('/player/' + player.uid).remove().then( x => {
          }, err => {
            this.isProcessingDelete = false;
              this.notificationBar.showUnknownError();
              return;
          })
        })
        this.db.database.app.database(environment.emsDatabaseURL).ref('/teams/' + this.teamID).remove().then( x => {
          this.router.navigate([ this.workspaceID + '/dashboard']);
          this.notificationBar.show("Team deleted!");
        }, err => {
            this.notificationBar.showUnknownError();
        })
      }
      this.isProcessingDelete = false;
    });
  }

  async duplicateTeam() {
    this.isProcessingDuplication = true;
    var dialogRef = this.dialog.open(ConfirmDialogDialog, {
      data: {
        actionType: "confirm",
        message: "Are you sure you want to duplicate " + this.teamGroup.controls.nameControl.value + "?",
      }
    });
    return dialogRef.afterClosed().toPromise().then(async res => {
      if (res == true) { 
        var workspaceID = this.workspaceID;
        var logo = null;
        if (this.teamGroup.controls.logoControl.value) {
          logo = new URL(this.teamGroup.controls.logoControl.value).pathname.slice(1);
        }
        var name = null;
        if (this.teamGroup.controls.nameControl.value) {
          name = "(Copy - " + this.generateDuplicateID() + ") " + this.teamGroup.controls.nameControl.value;
        }
        var tricode = null;
        if (this.teamGroup.controls.tricodeControl.value) {
          tricode = this.teamGroup.controls.tricodeControl.value;
        }
        var competition = null;
        if (this.teamGroup.controls.competitionControl.value) {
          competition = this.teamGroup.controls.competitionControl.value;
        }
        var coach = null;
        if (this.teamGroup.controls.coachControl.value) {
          coach = this.teamGroup.controls.coachControl.value;
        }
        var coachPicture = null;
        if (this.teamGroup.controls.coachpictureControl.value) {
          coachPicture = new URL(this.teamGroup.controls.coachpictureControl.value).pathname.slice(1);
        }
        var asstCoach = null;
        if (this.teamGroup.controls.asscoachControl.value) {
          asstCoach = this.teamGroup.controls.asscoachControl.value;
        }
        var asstCoachPicture = null;
        if (this.teamGroup.controls.asscoachpictureControl.value) {
          asstCoachPicture = new URL(this.teamGroup.controls.asscoachpictureControl.value).pathname.slice(1);
        }
        var notes = null;
        if (this.teamGroup.controls.notesControl.value) {
          notes = this.teamGroup.controls.notesControl.value;
        }
        var playerList = new Array<emsPlayer>();
        if (this.playerList.length > 0) {
          playerList = this.playerList;
        }
        var workspaceID = this.workspaceID;
        this.fireFunctions.httpsCallable("esportsms-duplicateTeam")({workspaceID, logo, name, tricode, competition, coach, coachPicture, asstCoach, asstCoachPicture, notes, playerList}).subscribe( res => {
          this.notificationBar.show("Team duplicated!");
          this.router.navigate([this.workspaceID, 't', res]);
          console.log(res)
          }, err => {
          if (err.code == "functions/permission-denied") {
            this.notificationBar.show("Missing permission!");
          }
          else {
            this.notificationBar.showUnknownError();
          }
        });
      }
      this.isProcessingDuplication = false;
    });
  }

  generateDuplicateID(): string {
    const chars = '0123456789';
    return Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  }
}